// Kit completeness on the client.
//
// The backend derives `kit_total` / `kit_ready` / `kit_status` and never stores
// them (woodwall/constructibles/kits.py). This file holds the one thing the
// client decides: which single label a row shows, composed from the stored
// production status and the derived kit status.
//
// One label per row, never two. On the production viewer the kit is not a
// second signal beside the status — it is one more stage of the one status the
// row reports, and it sits where `waiting` used to: a constructible missing 13
// parts is not waiting its turn, it cannot be built.

export const KIT_NONE = "none";
export const KIT_WAITING = "waiting";
export const KIT_PARTIAL = "partial";
export const KIT_KITTED = "kitted";

export const MODE_OFF = "off";
export const MODE_MANUAL = "manual";
export const MODE_AUTO = "auto";

// `always_kitted` is the stored value of the disabled state, kept for backward
// compatibility (31 of 32 companies hold it) and read as `off`.
export function kitMode(value) {
    return value === MODE_MANUAL || value === MODE_AUTO ? value : MODE_OFF;
}

export function kitTrackingOn(value) {
    return kitMode(value) !== MODE_OFF;
}

export function kitTotal(constructible) {
    return Number(constructible?.kit_total ?? 0);
}

export function kitReady(constructible) {
    return Number(constructible?.kit_ready ?? 0);
}

export function kitLeft(constructible) {
    return Math.max(0, kitTotal(constructible) - kitReady(constructible));
}

export function kitStatus(constructible) {
    return constructible?.kit_status || KIT_NONE;
}

export function isKitted(constructible) {
    return kitStatus(constructible) === KIT_KITTED;
}

/** "13 parts left", with the singular spelled out rather than pluralised. */
export function partsLeftText(count, t) {
    if (count === 1) return t("kit_one_part_left");
    return t("kit_parts_left").replace("{n}", count);
}

/**
 * The single label a production row shows, or null when the row has nothing to
 * say about its kit.
 *
 * Returns null for: kit tracking off; a built constructible (the ready chip
 * already says it); a constructible with no kit at all — claiming a complete
 * kit over an empty set is the same `0 === 0` lie that made the old rule report
 * every part-less constructible as kitted; and one that is under way with a
 * complete kit, where the station chips carry the row.
 */
export function kitLabel(constructible, mode, t) {
    if (!kitTrackingOn(mode)) return null;
    if (constructible?.status === "ready") return null;
    if (kitTotal(constructible) <= 0) return null;

    const kitted = isKitted(constructible);

    if (constructible?.status === "working") {
        // Started with parts missing: the one anomaly worth flagging, and the
        // only case where the row carries both a kit label and station chips.
        if (kitted) return null;
        return {
            key: "short",
            text: partsLeftText(kitLeft(constructible), t),
            color: "error",
        };
    }

    if (kitted) {
        return { key: KIT_KITTED, text: t("kit_status_kitted"), color: "info" };
    }

    return {
        key: kitStatus(constructible),
        text: partsLeftText(kitLeft(constructible), t),
        // Grey while nothing is cut yet, amber once cutting has started.
        color: kitStatus(constructible) === KIT_WAITING ? "grey" : "warning",
    };
}

/**
 * The cell of the Kit column in the constructibles manager.
 *
 * A table sorts and filters, so the cell stays a fraction and never the word:
 * the ratio is the sortable fact and the column header already says Kit. The
 * word belongs on the shop-floor row, where there is one label and no header to
 * lean on.
 */
export function kitFraction(constructible, t) {
    const total = kitTotal(constructible);
    if (total <= 0) return { text: "—", color: "", title: t("kit_no_parts") };

    const ready = kitReady(constructible);
    const text = `${ready}/${total}`;

    if (isKitted(constructible)) {
        const by_hand = constructible?.kit_override && ready < total;
        return {
            text: `✓ ${text}`,
            color: "success",
            title: by_hand ? t("kit_marked_by_hand") : "",
        };
    }
    return { text, color: ready > 0 ? "warning" : "", title: "" };
}

/**
 * Whether starting this constructible deserves a warning.
 *
 * True only when the company tracks kits, the constructible has a kit, and that
 * kit is incomplete. It is a warning and never a block: a shop floor that does
 * not mark parts ready must never be stopped by a count nobody keeps, which is
 * why the gate is soft and why it is gated on the company mode rather than on a
 * setting of its own.
 */
export function needsKitWarning(constructible, mode) {
    if (!kitTrackingOn(mode)) return false;
    if (kitTotal(constructible) <= 0) return false;
    return !isKitted(constructible);
}

/**
 * Sort key for the Kit column.
 *
 * Ascending reads as: nothing to report, then least cut, then most cut, then
 * complete. `-1` for a constructible with no kit puts it at the bottom of a
 * descending sort, where "closest to ready first" is the useful order.
 */
export function kitSortValue(constructible) {
    const total = kitTotal(constructible);
    if (total <= 0) return -1;
    // A complete kit is 1, and a partial one can only ever reach
    // (total - 1) / total, so the two never tie.
    if (isKitted(constructible)) return 1;
    return kitReady(constructible) / total;
}
